import React from "react";
import {
  CreateIleInput,
  IleObjectiveInput,
  useCreateStandaloneIleMutation,
  VideoDataInput,
} from "@axio/graphql/generated/graphql";
import InteractiveLearningFormDataContainer from "./InteractiveLearningFormDataContainer";

type InteractiveLearningLTIFormContainerProps = {
  onSuccess: (ileId: string, title: string) => void;
  onError: (error: Error) => void;
  onCancel?: () => void;
  initialValues?: Partial<InteractiveLearningFormPayload>;
  currentDomain?: string;
  imageGenerationSupportFlag?: boolean;
};

type InteractiveLearningObjectivePayload = {
  objective: string;
  sequence?: number;
  evalCriteria?: string[];
};

type InteractiveLearningVideoPayload = {
  videoId: string;
  objectiveText: string;
  objectiveSequence: number;
  isTeacherAdded?: boolean;
};

type InteractiveLearningFormPayload = {
  title: string;
  description?: string;
  context?: string;
  objectives: InteractiveLearningObjectivePayload[];
  videoPool?: InteractiveLearningVideoPayload[];
  ileType?: CreateIleInput["ileType"];
};

const getErrorFromUnknown = (error: unknown) => {
  if (error instanceof Error) {
    return error;
  }
  return new Error("Failed to create interactive learning experience");
};

const mapObjectives = (objectives: InteractiveLearningObjectivePayload[] = []): IleObjectiveInput[] =>
  objectives.map((objective, index) => ({
    objective: objective.objective,
    sequence: objective.sequence ?? index + 1,
    evalCriteria: objective.evalCriteria || [],
  }));

const mapVideoPool = (videoPool: InteractiveLearningVideoPayload[] = []): VideoDataInput[] =>
  videoPool.map((video) => ({
    videoId: video.videoId,
    objectiveText: video.objectiveText,
    objectiveSequence: video.objectiveSequence,
    isTeacherAdded: Boolean(video.isTeacherAdded),
  }));

const InteractiveLearningLTIFormContainer = ({
  onSuccess,
  onError,
  onCancel,
  initialValues,
  currentDomain,
  imageGenerationSupportFlag = false,
}: InteractiveLearningLTIFormContainerProps) => {
  const [createIleMutation, { loading: isSubmitting }] = useCreateStandaloneIleMutation();

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleSave = async (activityDataInput: unknown) => {
    try {
      const activityData = activityDataInput as InteractiveLearningFormPayload;
      const ileInput: CreateIleInput = {
        title: activityData.title,
        description: activityData.description || "",
        context: activityData.context || "",
        objectives: mapObjectives(activityData.objectives),
        videoPool: mapVideoPool(activityData.videoPool),
      };

      if (activityData.ileType || initialValues?.ileType) {
        ileInput.ileType = activityData.ileType || initialValues?.ileType;
      }

      const response = await createIleMutation({
        variables: {
          ile: ileInput,
        },
      });

      const createResponse = response.data?.createILE;
      if (!createResponse?.success) {
        throw new Error(createResponse?.message || "Failed to create interactive learning experience");
      }

      const createdIle = createResponse.data?.[0];
      if (!createdIle?.id) {
        throw new Error("Interactive learning experience was created without an ID");
      }

      onSuccess(createdIle.id, createdIle.title);
    } catch (error) {
      onError(getErrorFromUnknown(error));
    }
  };

  return (
    <InteractiveLearningFormDataContainer
      activityData={initialValues}
      onSave={handleSave}
      onCancel={handleCancel}
      isSubmitting={isSubmitting}
      currentDomain={currentDomain}
      imageGenerationSupportFlag={imageGenerationSupportFlag}
    />
  );
};

export default InteractiveLearningLTIFormContainer;
